<script>
  import { untrack } from 'svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import EmptyState from '../../components/empty_state.svelte'
  import ActionToast from '../../components/quest_submissions/action_toast.svelte'
  import RejectModal from '../../components/quest_submissions/reject_modal.svelte'
  import { moderationBadge } from '../../lib/moderation_badge.svelte.js'
  import ActivityFeed from './activity_feed.svelte'
  import AttentionPanel from './attention_panel.svelte'
  import FeedReviewOverlay from './feed_review_overlay.svelte'
  import LiveStatusBar from './live_status_bar.svelte'
  import PulsePanel from './pulse_panel.svelte'
  import QueueTiles from './queue_tiles.svelte'
  import { createFeedActions } from './feed_actions.svelte.js'
  import { createModerationLive } from './moderation_live.svelte.js'

  let {
    activity = null,
    activity_error = null,
    feed: feedFilter = 'all',
    feed_filters = [],
    rejection_categories = [],
    curation_levels = []
  } = $props()

  const BASE_TITLE = 'Moderation · Zupp Admin'

  let toast = $state(null)
  let reviewStartId = $state(null)

  const live = createModerationLive()
  const feed = createFeedActions({
    onChanged: live.refresh,
    onError: (title, text) => (toast = { type: 'error', title, text })
  })

  // A failed refresh arrives as a null `activity`; keep showing the last good one.
  let current = $state.raw(untrack(() => activity))

  $effect(() => {
    if (!activity) return
    current = activity
    untrack(() => {
      live.ingest(activity)
      moderationBadge.set(activity.pending_total)
    })
  })

  $effect(() => live.start())

  // Follows the badge count, which keeps refreshing while this tab is hidden.
  $effect(() => {
    const total = moderationBadge.count ?? 0
    const previous = document.title
    document.title = total > 0 ? `(${total}) ${BASE_TITLE}` : BASE_TITLE
    return () => (document.title = previous)
  })

  let rejectOpen = $state(false)
  $effect(() => {
    rejectOpen = !!feed.rejectTarget
  })
  $effect(() => {
    if (!rejectOpen && untrack(() => feed.rejectTarget)) feed.cancelReject()
  })

  // The reject dialog owns the screen while it is up — never stack the
  // full-screen surface on top of it.
  const openSubmission = (event) => {
    if (!feed.rejectTarget && event.quest_submission) reviewStartId = event.quest_submission.id
  }

  const closeReview = () => {
    reviewStartId = null
    live.refresh()
  }

  const hasQueues = $derived(!!current && Object.values(current.queue).some(Boolean))
</script>

<Layout>
  <PageHeader title="Moderation" subtitle="Live activity across the review queues">
    {#snippet actions()}
      <LiveStatusBar {live} undoCount={feed.undoStack.length} onUndoLast={feed.undoLast} />
    {/snippet}
  </PageHeader>

  <ActionToast {toast} ondismiss={() => (toast = null)} />

  {#if !current}
    <div class="alert alert-error" role="alert">Error loading moderation activity: {activity_error || 'unknown error'}</div>
  {:else if !hasQueues}
    <EmptyState
      icon="moderation"
      title="No moderation queues available"
      description="You don't have access to any moderation queues. Ask an admin to grant the required abilities."
    />
  {:else}
    <QueueTiles queue={current.queue} />

    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
      <div class="xl:col-span-2 min-w-0">
        <ActivityFeed activity={current} filter={feedFilter} filters={feed_filters} {live} {feed} onOpen={openSubmission} />
      </div>
      <div class="space-y-6">
        <AttentionPanel attention={current.attention} queue={current.queue} total={current.attention_total} />
        <PulsePanel pulse={current.pulse} />
      </div>
    </div>
  {/if}

  <RejectModal
    bind:open={rejectOpen}
    questPrompt={feed.rejectTarget?.quest_submission?.quest?.prompt}
    categories={rejection_categories}
    onconfirm={feed.confirmReject}
  />

  {#if reviewStartId}
    <FeedReviewOverlay
      events={current.events}
      queueIds={current.review_queue_ids}
      startId={reviewStartId}
      rejectionCategories={rejection_categories}
      curationLevels={curation_levels}
      onExit={closeReview}
      onDecision={(id) => feed.noteSubmissionReview(id)}
      onUndo={feed.noteSubmissionUnreview}
    />
  {/if}
</Layout>
